<template>
  <permission-gate
    v-slot="{ canEdit }"
    moduleKey="orden-produccion"
    :showNoEditAlert="true"
  >
    <main-breadcrumb
      :items="items"
      showBackButton
    ></main-breadcrumb>
    <main-buttons-bar :align="'space-between'">
      <template #left>
        <custom-title>Edición de orden de producción</custom-title>
      </template>
      <template
        v-if="ordenProduccion?.Codigo"
        #right
      >
        <el-button
          type="primary"
          size="default"
          :icon="IconChartTimelineVariantShimmer"
          @click="verResultadosProduccion"
        >
          Ver cálculos productivos
        </el-button>
        <el-button
          type="default"
          size="default"
          :icon="IconCopy"
          :disabled="!ordenProduccion?.Codigo"
          @click="openCloneModal"
        >
          Duplicar OP
        </el-button>
      </template>
    </main-buttons-bar>

    <loading-overlay :show="isLoading" />
    <div
      v-if="accessDenied"
      style="padding: 40px; text-align: center"
    >
      <el-empty
        description="No se encontró la orden de producción o no tienes permisos para visualizarla."
      >
        <el-button
          type="primary"
          @click="router.push('/page/work-orders/list')"
        >
          Volver al listado
        </el-button>
      </el-empty>
    </div>
    <detail-container v-else>
      <div class="detail-container">
        <el-row :gutter="24">
          <el-alert
            v-if="ordenProduccion.isDesactualizado"
            class="alerta-banner-dark"
            type="warning"
            effect="dark"
            show-icon
            :closable="false"
          >
            <template #default>
              <div class="banner-content">
                <div class="banner-text">
                  <span class="banner-title">Versión de ET Desactualizada</span>
                  <span class="banner-description">
                    Esta orden de producción utiliza la versión
                    <strong>V{{ ordenProduccion.VersionET }}</strong
                    >. Existe una versión más reciente (<strong
                      >V{{ ordenProduccion.versionVigente }}</strong
                    >) disponible.
                  </span>
                </div>
                <el-button
                  class="banner-button"
                  size="default"
                  @click="abrirDiferenciasEt"
                >
                  <template #icon>
                    <IconVectorDifference />
                  </template>
                  Ver cambios
                </el-button>
              </div>
            </template>
          </el-alert>
          <custom-data
            :label="'Nro de orden'"
            :spanColumn="3"
            :data="ordenProduccion?.Codigo"
          ></custom-data>
          <el-col
            v-if="ordenProduccion?.CodigoCotizacion"
            :span="3"
          >
            <div class="label">Nro. de Cotización</div>
            <router-link
              :to="`/page/prices/edit/${ordenProduccion.CodigoCotizacion}`"
              class="cotizacion-link"
            >
              {{ ordenProduccion.CodigoCotizacion }}
            </router-link>
          </el-col>
          <el-col :span="7">
            <div class="custom-label">Cliente/Prospecto</div>
            <el-autocomplete
              v-model="ordenProduccion.Cliente.Descripcion"
              size="default"
              :value-key="'Descripcion'"
              :class="{ 'input-error': !validarOrdenProduccionCampos.CodigoCliente }"
              :fetch-suggestions="handleSearch"
              clearable
              placeholder="Ingresar el cliente"
              style="width: 100%"
              @select="handleClientSelect"
              @clear="handleClientClear"
            />
          </el-col>
          <el-col :span="14">
            <input-additional-clients v-model="ordenProduccion.OrdenProduccionClientes" />
          </el-col>
        </el-row>
        <el-row :gutter="24">
          <custom-select
            v-model="ordenProduccion.Estado"
            :label="'Estado'"
            :spanColumn="4"
            :settings="{
              placeholder: 'Seleccionar estado',
              options: estadoSolicitudList,
              disabled: true,
            }"
            @update:modelValue="handleEstado"
          >
          </custom-select>
          <custom-data
            :label="'Fecha estado'"
            :spanColumn="3"
            :data="
              ordenProduccion.FechaEstado ? getDate('dd/mm/yyyy', ordenProduccion.FechaEstado) : ''
            "
          ></custom-data>
          <custom-data
            :label="'Fecha creacion'"
            :spanColumn="3"
            :data="
              ordenProduccion.FechaCreacion
                ? getDate('dd/mm/yyyy', ordenProduccion.FechaCreacion)
                : ''
            "
          />
          <custom-data
            :label="'Usuario Creación'"
            :spanColumn="3"
            :data="`${ordenProduccion.UsuarioCreacion?.Nombre || ''} ${
              ordenProduccion.UsuarioCreacion?.Apellido || ''
            }`"
          ></custom-data>
        </el-row>
        <el-row :gutter="24">
          <custom-input
            v-model.number="ordenProduccion.NumeroPedido"
            :label="'Nro de Pedido'"
            :spanColumn="5"
            :settings="{
              placeholder: 'Nro de Pedido',
            }"
          ></custom-input>
          <custom-input
            v-model.number="ordenProduccion.OrdenCompra"
            :label="'Nro Orden de Compra'"
            :spanColumn="5"
            :settings="{
              placeholder: 'Nro Orden de Compra',
            }"
          ></custom-input>
          <custom-input-number
            v-model.number="ordenProduccion.ToleranciaMinima"
            :label="'% Tolerancia Mínima'"
            :spanColumn="4"
            :settings="{
              placeholder: '% Tolerancia Mínima',
              min: 0,
              precision: 2,
              step: 0.01,
              controls: false,
            }"
          />
          <custom-input-number
            v-model.number="ordenProduccion.ToleranciaMaxima"
            :label="'% Tolerancia Máxima'"
            :spanColumn="4"
            :settings="{
              placeholder: '% Tolerancia Máxima',
              min: 0,
              precision: 2,
              step: 0.01,
              controls: false,
            }"
          />
          <custom-data
            :label="'Fecha Modificación'"
            :spanColumn="3"
            :data="
              ordenProduccion.FechaActualizacion
                ? getDate('dd/mm/yyyy', ordenProduccion.FechaActualizacion)
                : ''
            "
          ></custom-data>
          <custom-data
            :label="'Usuario Modificación'"
            :spanColumn="3"
            :data="`${ordenProduccion.UsuarioActualizacion?.Nombre || ''} ${
              ordenProduccion.UsuarioActualizacion?.Apellido || ''
            }`"
          ></custom-data>
        </el-row>
        <el-row :gutter="24">
          <custom-input
            v-model="ordenProduccion.Referencia"
            :label="'Descripción'"
            :spanColumn="10"
            :settings="{
              placeholder: 'Ingrese la descripción del producto',
              inputClass: { 'input-error': !validarOrdenProduccionCampos.Referencia },
            }"
          ></custom-input>
          <custom-select
            v-if="false"
            v-model="ordenProduccion.Moneda"
            :label="'Moneda'"
            :spanColumn="4"
            :settings="{
              placeholder: 'Seleccionar moneda',
              options: monedaList,
            }"
          >
          </custom-select>
        </el-row>
        <el-row :gutter="24">
          <el-col :span="4">
            <div class="custom-label">Adjuntos</div>
            <el-upload
              class="upload-list"
              :file-list="fileList"
              :auto-upload="true"
              multiple
              :http-request="uploadAdjuntoOrdenProduccion"
              accept=".jpg,.png,.pdf,.txt"
              :on-remove="handleRemoveOrdenProduccion"
              :on-preview="handlePreviewAdjunto"
            >
              <el-button
                class="button-container-upload"
                type="primary"
                size="default"
                ><icon-upload></icon-upload> Subir adjuntos</el-button
              >
            </el-upload>
          </el-col>
          <custom-select
            v-model="ordenProduccion.Usabilidad"
            :label="'Usabilidad'"
            :spanColumn="4"
            :settings="{
              placeholder: 'Seleccionar aplicación',
              options: usabilidadLista,
            }"
            @update:modelValue="handleUsabilidad"
          ></custom-select>
          <custom-select
            v-model="ordenProduccion.UnidadVenta"
            :label="'Unidad venta'"
            :spanColumn="6"
            :settings="{
              placeholder: 'Seleccionar unidad',
              inputClass: { 'input-error': !validarOrdenProduccionCampos.CodigoUnidadVenta },
              options: unidadesMedida,
            }"
            @update:modelValue="handleUnidadVenta"
          ></custom-select>
        </el-row>
        <el-row
          :gutter="24"
          style="margin-top: 20px"
        >
          <el-col>
            <div class="custom-label">Cantidades a producir</div>
            <div class="quantity-inputs">
              <div class="container-quantity-inputs">
                <custom-input-number
                  v-model="ordenProduccion.OrdenProduccionCantidad[0].Cantidad"
                  class="input-number"
                  :spanColumn="3"
                  :settings="{
                    placeholder: 'Cantidad',
                    inputClass: {
                      'input-error': !validarOrdenProduccionCampos.OrdenProduccionCantidad,
                    },
                    min: 0,
                    precision: precisionCantidades,
                    step: 0.01,
                    controls: false,
                  }"
                  @change="workOrderStore.autoCompletarCantidadUnica"
                />
              </div>
            </div>
          </el-col>
        </el-row>
        <input-pedidos
          v-if="mostrarGestionPedidos"
          :pedidos="ordenProduccion.ImportacionPedidoCantidadOP"
          :codigoCliente="(ordenProduccion.Cliente && ordenProduccion.Cliente.Codigo) || null"
          :codigoElementoGrupo="
            (ordenProduccion &&
              ordenProduccion.OrdenProduccionElemento &&
              ordenProduccion.OrdenProduccionElemento[0] &&
              ordenProduccion.OrdenProduccionElemento[0].ElementoTipo &&
              ordenProduccion.OrdenProduccionElemento[0].ElementoTipo.ElementoGrupoDetalle &&
              ordenProduccion.OrdenProduccionElemento[0].ElementoTipo.ElementoGrupoDetalle[0] &&
              ordenProduccion.OrdenProduccionElemento[0].ElementoTipo.ElementoGrupoDetalle[0]
                .CodigoElementoGrupo) ||
            null
          "
          @import-order="workOrderStore.importOrder"
        />

        <InputProductosTerminados
          :productos="ordenProduccion.OrdenProduccionProducto"
          :codigoCliente="(ordenProduccion.Cliente && ordenProduccion.Cliente.Codigo) || null"
          :clientes="clientesParaProductos"
          :codigoElementoGrupo="elementoGrupo?.Codigo || null"
          :hasQuantityError="!workOrderStore.cantidadesValidadas"
          :precision="precisionCantidades"
          @add-producto="workOrderStore.addProducto"
          @remove-producto="workOrderStore.removeProducto"
          @update-producto="workOrderStore.updateProducto"
        />

        <input-deliveries
          :deliveries="ordenProduccion.OrdenProduccionEntregas"
          :precision="precisionCantidades"
          @add-delivery="workOrderStore.addEntrega"
          @remove-delivery="workOrderStore.removeEntrega"
          @update-delivery="workOrderStore.updateEntrega"
        />

        <div
          v-for="(elemento, index) in modelo"
          :key="index"
        >
          <!-- <span v-if="getComponenteDinamico(elemento.ElementoTipo.CodigoElementoBase) == ''">
              Componente no encontrado {{ elemento.ElementoTipo.CodigoElementoBase }}
            </span> -->
          <component
            :is="getComponenteDinamico(elemento.ElementoTipo.CodigoElementoBase)"
            v-if="configReady && elemento.ElementoTipo?.CodigoElementoBase"
            v-model="modelo[index]"
            :title="`${elemento.ElementoTipo.Descripcion} ${index + 1}`"
            :name="`${elemento.ElementoTipo.Descripcion}${index}`"
          />
        </div>

        <el-divider></el-divider>
        <el-row :gutter="20">
          <custom-input
            v-model="ordenProduccion.ObservacionesCotizacion"
            :label="'Observaciones de Cotización'"
            :spanColumn="12"
            :settings="{
              placeholder: 'Información recibida desde la Cotización',
              maxlength: 800,
              showWordLimit: true,
            }"
          ></custom-input>

          <custom-input
            v-model="ordenProduccion.Observaciones"
            :label="'Observaciones de ET'"
            :spanColumn="12"
            :settings="{
              placeholder: 'Información recibida desde la ET',
              maxlength: 800,
              showWordLimit: true,
            }"
          ></custom-input>
        </el-row>

        <el-row
          :gutter="20"
          style="margin-top: 1rem"
        >
          <custom-input
            v-model="ordenProduccion.ObservacionesProduccion"
            :label="'Observaciones de Producción'"
            :spanColumn="12"
            :settings="{
              placeholder: 'Observaciones de producción',
              maxlength: 800,
              showWordLimit: true,
            }"
          ></custom-input>

          <custom-input
            v-model="ordenProduccion.Notas"
            :label="'Notas Internas'"
            :spanColumn="12"
            :settings="{
              placeholder: 'Notas Internas',
              maxlength: 800,
              showWordLimit: true,
            }"
          ></custom-input>
        </el-row>
        <div
          v-if="canEdit"
          class="container"
        >
          <custom-confirm-buttons
            :disabled="
              ordenProduccion.Estado.Descripcion === 'Pendiente' ||
              ordenProduccion.Estado.Descripcion === 'A Cotizar'
                ? false
                : true
            "
            :confirmDisabled="ordenProduccion.Estado.Descripcion === 'Cotizada'"
            :showLoading="false"
            :confirmText="'Guardar'"
            :cancelText="'Cancelar'"
            :isSolicitud="true"
            @on-cancel="onCancel"
            @on-confirm="handleSave"
            @on-anular="onAnular"
          ></custom-confirm-buttons>
        </div>
        <div class="container footer-actions">
          <el-button
            type="primary"
            size="default"
            @click="handlePrint"
          >
            <icon-print /> Imprimir</el-button
          >
          <el-button
            v-if="canEdit"
            type="success"
            :disabled="isApproved"
            size="default"
            @click="handleApprove"
          >
            <icon-check /> Aprobar</el-button
          >
        </div>
      </div>
    </detail-container>
    <modal-duplicate-work-order
      v-if="modalDuplicateVisible"
      v-model="modalDuplicateVisible"
      :codigoOT="duplicateCodigoOT"
      :codigoUnidadVenta="ordenProduccion?.CodigoUnidadVenta ?? null"
      :isObsoleta="ordenProduccion?.isDesactualizado"
      @confirm="confirmDuplicate"
    />

    <floating-info-panel
      :panel-state="floatingPanel.panelState"
      :is-pinned="floatingPanel.isPinned"
      :is-minimized="floatingPanel.isMinimized"
      :active-tab="floatingPanel.activeTab"
      :datos-producto="resultadoProduccionOtStore.datosProducto"
      :datos-producto-live="datosProductoLive"
      :procesos="resultadoProduccionOtStore.resultadoDetalle?.procesos || []"
      :materiales="resultadoProduccionOtStore.resultadoDetalle?.materiales || []"
      :cantidades="resultadoProduccionOtStore.cantidades || []"
      :cantidad-seleccionada="resultadoProduccionOtStore.cantidadSeleccionada"
      cantidad-item-key="codigoOrdenProduccionCantidad"
      :is-stale="resultadoProduccionOtStore.isStale"
      :loading="resultadoProduccionOtStore.loading"
      @show="floatingPanel.show"
      @hide="floatingPanel.hide"
      @hide-now="floatingPanel.hideNow"
      @toggle-pin="floatingPanel.togglePin"
      @toggle-minimize="floatingPanel.toggleMinimize"
      @cantidad-change="handleFloatingCantidadChange"
      @tab-change="floatingPanel.setActiveTab"
    />

    <et-diferencias-modal
      v-model="modalDiferenciasEtVisible"
      :diferencias="diferenciasEt"
      :bloqueado-por-propuesta="bloqueadoPorPropuestaEt"
      :loading="loadingActualizarEt"
      @update="actualizarEt"
    />
  </permission-gate>
</template>

<script setup>
import {
  ref,
  reactive,
  onMounted,
  onBeforeMount,
  computed,
  watch,
  provide,
  onUnmounted,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import { storeToRefs } from 'pinia';
import {
  ElNotification,
  ElMessageBox,
} from 'element-plus';
import { useWorkOrderStore } from '@/views/work_orders/stores/work_order_store.js';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import { useMonedaStore } from '@/stores/moneda/moneda_store';
import { useRecursoStore } from '@/stores/recurso_store.js';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import { useUsabilidadStore } from '@/stores/usabilidad/usabilidad_store.js';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store.js';
import { useMateriaPrimaFamiliaStore } from '@/stores/materia_prima_familia/materia_prima_familia_store';
import { useProcesoStore } from '@/stores/proceso_store';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { Modules } from '@/enum/modules';
import {
  precisionCantidad,
  redondearCantidadesLista,
} from '@/shared/utils/cantidad_unidad_venta.js';
import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import DetailContainer from '@/shared/layout/detail/detail_container.vue';
import LoadingOverlay from '@/shared/LoadingOverlay.vue';
import CustomData from '@/shared/components/custom_data.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconUpload from '~icons/mdi/cloud-upload';
import IconPrint from '~icons/mdi/printer-outline';
import IconCheck from '~icons/mdi/check-bold';
import IconCopy from '~icons/mdi/content-copy';
import IconChartTimelineVariantShimmer from '~icons/mdi/chart-timeline-variant-shimmer';
import getDate from '@/shared/components/utils.js';
import IconVectorDifference from '~icons/mdi/vector-difference-ba';
import { useAdjuntosUpload } from '@/shared/composables/useAdjuntosUpload.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import inputAdditionalClients from '@/shared/components/input_additional_clients.vue';
import InputDeliveries from '@/shared/components/input_deliveries.vue';
import InputProductosTerminados from '@/shared/components/input_productos_terminados.vue';
import InputPedidos from '@/shared/components/input_pedidos.vue';
import { useElementos } from '@/shared/composables/useElementos.js';
import { useModulo } from '@/shared/composables/useModulo.js';
import { useElementoGrupoStore } from '@/stores/elemento_grupo/elemento_grupo_store';
import work_order_elemento_model from '../models/work_order_elemento_model.js';
import { usePrint } from '@/shared/composables/print';
import { ElementoBase } from '@/enum/elemento_base';
import { conAnchoMaterialExtrusion } from '@/shared/utils/extrusion_ancho_material.js';
import { describirCamposInvalidos } from '@/shared/utils/validacion_campos.js';
import { provideErrorRegistry } from '@/shared/composables/use_error_registry.js';
import { notificarErroresValidacion } from '@/shared/utils/notificar_errores_validacion.js';
import PermissionGate from '@/shared/components/permission_gate.vue';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';
import ModalDuplicateWorkOrder from '@/shared/components/modal_duplicate_work_order.vue';
import FloatingInfoPanel from '@/shared/components/floating_info_panel.vue';
import { useFloatingPanel } from '@/shared/composables/use_floating_panel.js';
import { useAnchoMinimoMM } from '@/shared/composables/use_ancho_minimo.js';
import { useResultadoProduccionOtStore } from '../resultado_produccion/stores/resultado_produccion_ot_store.js';
import EtDiferenciasModal from '@/shared/components/et_diferencias_modal.vue';

provide('currentModule', Modules.ORDEN_PRODUCCION);
const elementoBaseEtiquetas = computed(
  () => elementoBaseStore.elementoBaseConfig?.etiquetas ?? null,
);
provide('elementoBaseEtiquetas', elementoBaseEtiquetas);

const { tieneErrores, mensajes: mensajesErrores } = provideErrorRegistry();

const workOrderStore = useWorkOrderStore();
const prospectStore = useProspectStore();
const monedaStore = useMonedaStore();
const recursoStore = useRecursoStore();
const estadoSolicitudStore = useEstadoSolicitudStore();
const usabilidadStore = useUsabilidadStore();
const unidadMedidaStore = useUnidadMedidaStore();
const materiaPrimaFamiliaStore = useMateriaPrimaFamiliaStore();
const procesoStore = useProcesoStore();
const configuracionStore = useConfiguracionStore();
const elementoBaseStore = useElementoBaseStore();

const elementoGrupoStore = useElementoGrupoStore();
const { elementoGrupo } = storeToRefs(elementoGrupoStore);

const resultadoProduccionOtStore = useResultadoProduccionOtStore();
const floatingPanel = reactive(useFloatingPanel());

const { firstConfiguraciones, validarClave, uploadPublicFileHTTP } = configuracionStore;
const { firstEstadoSolicitud } = estadoSolicitudStore;
const { estadoSolicitudList } = storeToRefs(estadoSolicitudStore);
const {
  mostrarGestionPedidos,
  rangoMaximoToleranciaEntrega,
  rangoMinimoToleranciaEntrega,
  sellado,
  solapa,
} = storeToRefs(configuracionStore);
const { ordenProduccion, validarOrdenProduccionCampos, ordenProduccionListaCompentes } =
  storeToRefs(workOrderStore);
// El backend arma esto agrupando los materiales ya elegidos en TODO el documento por
// categoría (ver `agruparMaterialesSeleccionadosPorCategoria` en el server) — input_materiales.vue
// lo inyecta para poblar sus combos remotos con lo ya seleccionado, sin depender de qué página
// de la búsqueda tenga cargada en ese momento.
const materialesSeleccionadosPorCategoria = computed(
  () => ordenProduccion.value?.MaterialesSeleccionadosPorCategoria ?? {},
);
provide('materialesSeleccionadosPorCategoria', materialesSeleccionadosPorCategoria);
const { monedaList } = storeToRefs(monedaStore);
const { usabilidadLista } = storeToRefs(usabilidadStore);
const { unidadesMedida } = storeToRefs(unidadMedidaStore);
const precisionCantidades = computed(() =>
  precisionCantidad(ordenProduccion.value.CodigoUnidadVenta),
);
const { firstMoneda } = monedaStore;
const { searchProspectsHTTP } = prospectStore;
const {
  resetOrdenProduccion,
  resetValidaciones,
  updateOrdenProduccionHTTP,
  createOrdenProduccionHTTP,
  findOrdenProduccionPorCodigoHTTP,
  validarCampos,
  setValidacionOrdenProduccionCantidad,
  cloneOrdenProduccionHTTP,
  getDiferenciasEtHTTP,
  actualizarEtHTTP,
} = workOrderStore;
const route = useRoute();
const router = useRouter();
const modalDuplicateVisible = ref(false);
const duplicateCodigoOT = ref(null);
const accessDenied = ref(false);
const errorToleranciaMinima = ref('');
const errorToleranciaMaxima = ref('');
const ElementoTipoSeleccionado = ref({});
const configReady = ref(false);
const { modelo, initializeModelo, standardToModuleArray, isLoading } = useElementos();
const { getComponenteDinamico } = useModulo(Modules.ORDEN_PRODUCCION);
const aprobacionPorPasswordHabilitada = computed(
  () => configuracionStore.aprobacionPorPasswordHabilitada,
);

const isApproved = computed(
  () => ordenProduccion.value.Estado?.Descripcion?.toLowerCase() === 'aceptada',
);

const uploadToken = ref((crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()));
const API_BASE = import.meta.env.VITE_URL_BASE_API || import.meta.env.VITE_URL_BASE || '';

const ordenProduccionAdjuntoAdapter = {
  getAdjuntos: () => ordenProduccion.value.OrdenProduccionAdjunto,
  setAdjuntos: (next) => (ordenProduccion.value.OrdenProduccionAdjunto = next),

  getSubdir: () => {
    const code = ordenProduccion.value?.Codigo;
    return code ? `adjuntos/ordenProduccion` : `adjuntos/ordenProduccion/tmp/${uploadToken.value}`;
  },

  buildAdjunto: ({ relPath, nextItem }) => ({
    Codigo: null,
    CodigoOrdenProduccion: ordenProduccion.value?.Codigo ?? null,
    ItemAdjunto: nextItem,
    Url: relPath,
  }),
};

const clientesParaProductos = computed(() => {
  const set = new Set();

  const principal = Number(ordenProduccion.value?.Cliente?.Codigo);
  if (Number.isFinite(principal) && principal > 0) set.add(principal);

  const extra = ordenProduccion.value?.OrdenProduccionClientes;
  if (Array.isArray(extra)) {
    extra.forEach((c) => {
      const cod = Number(c?.CodigoCliente);
      if (Number.isFinite(cod) && cod > 0) set.add(cod);
    });
  }

  return Array.from(set);
});

const {
  fileList,
  uploadAdjunto: uploadAdjuntoOrdenProduccion,
  handleRemove: handleRemoveOrdenProduccion,
  hydrateFileListFromModel,
} = useAdjuntosUpload({
  uploadPublicFileHTTP,
  apiBase: API_BASE,
  adapter: ordenProduccionAdjuntoAdapter,
});

const handlePreviewAdjunto = (file) => {
  const url = file?.url;
  if (!url) return;
  window.open(url, '_blank', 'noopener');
};

const elementoActivo = computed(() => modelo.value?.[0] || {});
const { anchoMinimoMM } = useAnchoMinimoMM(elementoActivo);

const datosProductoLive = computed(() => {
  const elemento = modelo.value?.[0];

  if (!elemento) return {};

  return {
    anchoEje: elemento.MedidaEje || 0,
    largoDesarrollo: elemento.MedidaDesarrollo || 0,
    desarrolloImposicion: elemento.Desarrollo || 0,
    repAncho: elemento.EtiquetasEje || 0,
    repAlto: elemento.EtiquetasDesarrollo || 0,
    totalBocas: (elemento.EtiquetasEje || 1) * (elemento.EtiquetasDesarrollo || 1),
    laterales: elemento.Laterales || 0,
    anchoMaterial: elemento.AnchoMaterial || 0,
    anchoMinimo: anchoMinimoMM.value || 0,
    m2: ((elemento.MedidaDesarrollo || 0) * (elemento.AnchoMaterial || 0)) / 1000000,
  };
});

watch(
  () => {
    const el = modelo.value?.[0];
    if (!el) return null;
    return `${el.MedidaEje}-${el.MedidaDesarrollo}-${el.EtiquetasEje}-${el.EtiquetasDesarrollo}-${el.AnchoMaterial}-${el.Desarrollo}-${el.GapEje}-${el.GapDesarrollo}`;
  },
  (newVal, oldVal) => {
    if (oldVal !== null && newVal !== oldVal) {
      resultadoProduccionOtStore.isStale = true;
    }
  },
);

const handleFloatingCantidadChange = (codigoCantidad) => {
  if (ordenProduccion.value.Codigo) {
    resultadoProduccionOtStore.fetchResultadoPorCantidadHTTP(
      ordenProduccion.value.Codigo,
      codigoCantidad,
    );
  }
};

const verResultadosProduccion = () => {
  if (ordenProduccion.value?.Codigo) {
    router.push(`/page/work-orders/resultado-produccion/${ordenProduccion.value.Codigo}`);
  } else {
    useMessageError().open('Código de orden de producción no disponible');
  }
};

const openCloneModal = () => {
  duplicateCodigoOT.value = ordenProduccion.value?.Codigo ?? null;
  modalDuplicateVisible.value = !!duplicateCodigoOT.value;
};

const modalDiferenciasEtVisible = ref(false);
const diferenciasEt = ref([]);
const bloqueadoPorPropuestaEt = ref(false);
const loadingActualizarEt = ref(false);

const abrirDiferenciasEt = async () => {
  try {
    const data = await getDiferenciasEtHTTP(ordenProduccion.value.Codigo);
    diferenciasEt.value = data?.diferencias || [];
    bloqueadoPorPropuestaEt.value = !!data?.bloqueadoPorPropuesta;
    modalDiferenciasEtVisible.value = true;
  } catch (err) {
    console.error(err);
    useMessageError().open('Ocurrió un error al obtener las diferencias de la ET.');
  }
};

const actualizarEt = async ({ actualizarTodos, camposAActualizar }) => {
  loadingActualizarEt.value = true;
  try {
    await actualizarEtHTTP(ordenProduccion.value.Codigo, { actualizarTodos, camposAActualizar });
    await findOrdenProduccionPorCodigoHTTP(ordenProduccion.value.Codigo);
    await initializeModelo(ordenProduccion.value.OrdenProduccionElemento, Modules.ORDEN_PRODUCCION);
    sincronizarAtributosAdicionales(modelo.value);
    useMessageSuccess().open('Los datos se actualizaron correctamente.');
    modalDiferenciasEtVisible.value = false;
  } catch (err) {
    console.error(err);
    useMessageError().open('Ocurrió un error al actualizar los datos.');
  } finally {
    loadingActualizarEt.value = false;
  }
};
const confirmDuplicate = async ({ codigoOT, cantidad, actualizarET }) => {
  let opened = false;
  try {
    useLoading().openLoading();
    opened = true;

    const nueva = await cloneOrdenProduccionHTTP(codigoOT, cantidad, actualizarET);

    useMessageSuccess().open('La orden de producción se ha copiado correctamente.');

    modalDuplicateVisible.value = false;
    duplicateCodigoOT.value = null;

    // UX: llevarlo directo a la OT nueva
    if (nueva?.Codigo) {
      router.push(`/page/work-orders/edit/${nueva.Codigo}`);
    } else {
      // fallback: volver al listado
      router.push('/page/work-orders/list');
    }
  } catch (err) {
    console.error(err);
    useMessageError().open('Ha ocurrido un error al intentar copiar la orden de producción.');
  } finally {
    if (opened) useLoading().closeLoading();
  }
};

function setToleranciasFromClienteOrConfig(cliente) {
  const cMin = Number(cliente?.ToleranciaMinima);
  const cMax = Number(cliente?.ToleranciaMaxima);
  const useClientMin = Number.isFinite(cMin) && cMin > 0;
  const useClientMax = Number.isFinite(cMax) && cMax > 0;

  ordenProduccion.value.ToleranciaMinima = useClientMin ? cMin : rangoMinimoToleranciaEntrega.value;
  ordenProduccion.value.ToleranciaMaxima = useClientMax ? cMax : rangoMaximoToleranciaEntrega.value;
}

const handleClientClear = () => {
  ordenProduccion.value.Cliente = { Descripcion: '' };
  ordenProduccion.value.CodigoCliente = null;
  ordenProduccion.value.ToleranciaMinima = null;
  ordenProduccion.value.ToleranciaMaxima = null;
};

const validacionOrdenProduccionCantidad = computed(() => {
  return ordenProduccion.value.OrdenProduccionCantidad.map(
    (item) => item.Cantidad !== null && item.Cantidad > 0,
  );
});

watch(validacionOrdenProduccionCantidad, (nuevoValor) => {
  setValidacionOrdenProduccionCantidad(nuevoValor);
});

// local state
const items = [
  { label: 'Inicio', to: '/page/work-orders/list', replace: false },
  { label: 'Edición', to: null, replace: false },
];

// const getComponenteDinamico = (CodigoElementoTipo) => {
//   let Codigo
//   if (CodigoElementoTipo) {
//     Codigo = CodigoElementoTipo
//   } else {
//     Codigo = JSON.parse(localStorage.getItem('tipoProductoSeleccionado')).Codigo
//   }
//   const component = ordenProduccionListaCompentes.value.find((item) => item.id === Codigo)

//   return (component && component.componentNameEdit) || ''
// }

const handleSearch = async (query, cb) => {
  let results = [];
  if (query && query.length >= 3 && query !== 'null') {
    results = await searchProspectsHTTP(query);
  }
  cb(results);
};

const handleClientSelect = async (item) => {
  if (item && item.Codigo) {
    ordenProduccion.value.Cliente = item;
    ordenProduccion.value.CodigoCliente = item.Codigo;
    setToleranciasFromClienteOrConfig(item);
  }
};

// Método para manejar el cambio de estado
function handleEstado() {
  ordenProduccion.value.FechaEstado = getDate('yyyy/mm/dd');
  ordenProduccion.value.CodigoEstado = ordenProduccion.value.Estado.Codigo;
}
// Método para manejar el cambio de Usabilidad
function handleUsabilidad(item) {
  ordenProduccion.value.CodigoUsabilidad = item.Codigo;
}
// Método para manejar el cambio de Unidad Venta
function handleUnidadVenta(item) {
  ordenProduccion.value.CodigoUnidadVenta = item?.Codigo ?? null;
  const precision = precisionCantidad(ordenProduccion.value.CodigoUnidadVenta);
  const listas = [
    ordenProduccion.value.OrdenProduccionCantidad,
    ordenProduccion.value.OrdenProduccionEntregas,
    ordenProduccion.value.OrdenProduccionProducto,
  ];
  const huboCambios = listas
    .map((lista) => redondearCantidadesLista(lista, precision))
    .some(Boolean);
  if (huboCambios) {
    ElNotification({
      title: 'Cantidades',
      message: 'La unidad de venta no admite decimales: las cantidades se redondearon a enteros.',
      type: 'warning',
    });
  }
}

const onCancel = () => {
  resetOrdenProduccion();
  router.go(-1);
};

const onAnular = async () => {
  ordenProduccion.value.Estado = {
    Codigo: 5,
    Descripcion: '',
  };
  await handleSave();
};

function handlePrint() {
  usePrint().printReport('orden_produccion', {
    Codigo: ordenProduccion.value.Codigo,
  });
}

async function handleApprove() {
  if (!validarCampos()) {
    ElNotification({
      title: 'Revisar formulario',
      message: describirCamposInvalidos(workOrderStore.camposOrdenProduccionInvalidos),
      type: 'error',
    });
    return;
  }
  if (!ordenProduccion.value.Codigo) {
    ElNotification({
      title: 'Error',
      message: 'Primero debes guardar la orden antes de aprobarla.',
      type: 'warning',
    });
    return;
  }
  if (!workOrderStore.cantidadesValidadas) {
    ElNotification({
      title: 'Cantidades inconsistentes',
      message: 'La cantidad a producir no coincide con el detalle de productos.',
      type: 'error',
    });
    return;
  }
  try {
    if (aprobacionPorPasswordHabilitada.value) {
      const { value: password } = await ElMessageBox.prompt(
        'Ingrese la contraseña de aprobación',
        'Aprobar Orden de Producción',
        {
          inputType: 'password',
          inputPlaceholder: 'Contraseña',
          confirmButtonText: 'Aprobar',
          cancelButtonText: 'Cancelar',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          roundButton: false,
        },
      );
      const resp = await configuracionStore.validarClave({
        claveIngresada: password,
        claveInterna: 'clave_aprobacion',
      });
      if (!resp?.data?.valido) {
        ElNotification({
          title: 'Contraseña',
          message: 'La contraseña ingresada es incorrecta',
          type: 'error',
        });
        return;
      }
    } else {
      await ElMessageBox.confirm(
        '¿Confirmás la aprobación de la Orden de Producción?',
        'Confirmar',
        { type: 'warning', confirmButtonText: 'Aprobar', cancelButtonText: 'Cancelar' },
      );
    }
  } catch (err) {
    const canceled = err === 'cancel' || err?.action === 'cancel' || err?.action === 'close';
    if (!canceled) {
      console.error(err);
      useMessageError().open('Ha ocurrido un error al intentar aprobar la orden de producción.');
    }
    return;
  }

  let opened = false;
  try {
    useLoading().openLoading();
    opened = true;
    ordenProduccion.value.CodigoEstado = 3;
    ordenProduccion.value.Estado = { Codigo: 3, Descripcion: 'Aceptada' };
    ordenProduccion.value.FechaEstado = new Date();
    await updateOrdenProduccionHTTP();
    useMessageSuccess().open('La orden de producción se aprobó exitosamente!');
    router.go(-1);
  } catch (err) {
    console.error(err);
    useMessageError().open('Ha ocurrido un error al intentar aprobar la orden de producción.');
  } finally {
    if (opened) useLoading().closeLoading();
  }
}

function sincronizarAtributosAdicionales(elementos) {
  if (!Array.isArray(elementos)) return;

  elementos.forEach((elemento) => {
    elemento.Secciones?.forEach((seccion) => {
      const procesos = seccion.ElementoProceso || seccion.elementoProceso || [];
      procesos.forEach((proceso) => {
        proceso.CategoriasMaterial?.forEach((categoria) => {
          categoria.ElementoMateriales?.forEach((material) => {
            if (
              material.AtributosAdicionales &&
              typeof material.AtributosAdicionales === 'object'
            ) {
              Object.assign(material, material.AtributosAdicionales);
            }
          });
        });
      });
    });
  });
}

/**
 * Serializa atributos dinámicos en el modelo antes de guardar
 * Crea un objeto AtributosAdicionales con todos los atributos visibles
 */
function serializeAtributosEnModelo(elementos) {
  if (!Array.isArray(elementos)) return;

  elementos.forEach((elemento) => {
    if (!elemento.Secciones) return;

    elemento.Secciones.forEach((seccion) => {
      const procesos = seccion.ElementoProceso || seccion.elementoProceso || [];
      procesos.forEach((proceso) => {
        // Iterar categorías y materiales
        if (proceso.CategoriasMaterial && Array.isArray(proceso.CategoriasMaterial)) {
          proceso.CategoriasMaterial.forEach((categoria) => {
            const materiales = categoria.ElementoMateriales || [];
            materiales.forEach((material) => {
              // Usar la referencia privada de atributos dinámicos guardada en el material
              const atributosClaves = material._atributosDinamicos;

              if (!atributosClaves || atributosClaves.length === 0) {
                // No hay atributos dinámicos para este material
                material.AtributosAdicionales = null;
                return;
              }

              const atributosAdicionales = {};
              let tieneValores = false;

              // Recopilar TODOS los atributos visibles con sus valores
              atributosClaves.forEach((clave) => {
                const valor = material[clave];
                // Incluir el atributo incluso si está vacío (para saber que es visible)
                atributosAdicionales[clave] = valor !== undefined ? valor : null;
                if (valor !== undefined && valor !== null && valor !== '') {
                  tieneValores = true;
                }
              });

              // Siempre incluir AtributosAdicionales si hay atributos configurados
              material.AtributosAdicionales = atributosAdicionales;
            });
          });
        }
      });
    });
  });
}

// Método para manejar el evento de guardar, cuando tiene fechaCreacion es porque es una modificacion.
const handleSave = async () => {
  // SERIALIZAR ATRIBUTOS DINÁMICOS ANTES DE CONVERTIR
  serializeAtributosEnModelo(modelo.value);

  // COTI-507: Para extrusion, el ancho de material sale de las bobinas del proceso.
  modelo.value = conAnchoMaterialExtrusion(
    modelo.value,
    ElementoTipoSeleccionado.value.CodigoElementoBase,
  );

  const elementosMapeados = standardToModuleArray(modelo.value, Modules.ORDEN_PRODUCCION);
  elementosMapeados.forEach((elemento) => {
    if (Array.isArray(elemento.OrdenProduccionElementoProceso)) {
      elemento.OrdenProduccionElementoProceso = elemento.OrdenProduccionElementoProceso.filter(
        (proceso) => !proceso.MarcadoParaEliminacion,
      );
    }
  });

  ordenProduccion.value.OrdenProduccionElemento = elementosMapeados;

  if (!validarCampos()) {
    ElNotification({
      title: 'Revisar formulario',
      message: describirCamposInvalidos(workOrderStore.camposOrdenProduccionInvalidos),
      type: 'error',
    });
    return;
  }
  const tieneErrorAnchoMaterial = modelo.value.some(
    (elemento) => elemento.ErrorAnchoMaterial === true,
  );

  if (tieneErrorAnchoMaterial) {
    ElNotification({
      title: 'Validación de Item',
      message:
        'El ancho del item es menor al ancho mínimo necesario para realizar el trabajo, por favor revise.',
      type: 'error',
    });
    return;
  }

  // COTI-633: registro de errores reportados por los componentes hijos (procesos,
  // materiales, pliego vs. máximo de máquina...) — reemplaza los chequeos puntuales
  // que antes vivían acá uno por uno.
  if (tieneErrores.value) {
    notificarErroresValidacion(mensajesErrores.value);
    return;
  }

  if (!workOrderStore.cantidadesValidadas) {
    ElNotification({
      title: 'Cantidades inconsistentes',
      message: 'La cantidad a producir no coincide con el detalle de productos.',
      type: 'error',
    });
    return;
  }
  try {
    useLoading().openLoading();

    let codigoSaved = ordenProduccion.value.Codigo;

    if (!codigoSaved) {
      const response = await createOrdenProduccionHTTP();
      codigoSaved = response?.data?.Codigo || response?.Codigo;
    } else {
      await updateOrdenProduccionHTTP();
    }
    if (codigoSaved) {
      await findOrdenProduccionPorCodigoHTTP(codigoSaved);
      await initializeModelo(
        ordenProduccion.value.OrdenProduccionElemento,
        Modules.ORDEN_PRODUCCION,
      );
      await resultadoProduccionOtStore.fetchResultadosHTTP(codigoSaved);
      if (!route.params.id) {
        router.replace({ path: `/page/work-orders/edit/${codigoSaved}` });
      }
    }

    useMessageSuccess().open(`Los datos se guardaron exitosamente!`);
  } catch (err) {
    console.log('Error al guardar:', err);
    useMessageError().open(`Ha ocurrido un error al intentar guardar los datos.`);
  } finally {
    useLoading().closeLoading();
  }
};

onMounted(() => {
  ordenProduccion.value.CodigoEstado = ordenProduccion.value.Estado?.Codigo;
  const id = Number(route.params?.id || 0);
  if (!id) {
    ordenProduccion.value.FechaEstado = getDate('yyyy/mm/dd');
  }
});

async function loadWorkOrder(id) {
  accessDenied.value = false;
  configReady.value = false;

  await configuracionStore.findAllConfiguracionesHTTP({ page: 1 });
  firstEstadoSolicitud();
  resetValidaciones();
  firstMoneda();
  recursoStore.firstRecurso();

  const codigo = Number(id);
  const isEdit = Number.isFinite(codigo) && codigo > 0;

  if (isEdit) {
    try {
      await findOrdenProduccionPorCodigoHTTP(codigo);
      if (!ordenProduccion.value || !ordenProduccion.value.Codigo) {
        throw new Error('La OT no se cargó correctamente');
      }
      ElementoTipoSeleccionado.value =
        ordenProduccion.value?.OrdenProduccionElemento?.[0]?.ElementoTipo ?? {};
      hydrateFileListFromModel();
      resultadoProduccionOtStore.fetchResultadosHTTP(ordenProduccion.value.Codigo);
    } catch (error) {
      console.error('Acceso denegado o OT no encontrada:', error);
      accessDenied.value = true;
      useLoading().closeLoading();
      return;
    }
  } else {
    ElementoTipoSeleccionado.value =
      elementoGrupo.value?.ElementoGrupoDetalle?.[0]?.ElementoTipo ?? {};

    if (!elementoGrupo.value || !elementoGrupo.value.Codigo) {
      ElNotification({
        title: 'Error',
        message: 'Debe seleccionar un grupo de elementos antes de crear una orden de produccion.',
        type: 'error',
      });
      router.push('/page/work-orders/list');
      return;
    }

    ordenProduccion.value.OrdenProduccionElemento = elementoGrupo.value.ElementoGrupoDetalle.map(
      (egd) => ({
        ...JSON.parse(JSON.stringify(work_order_elemento_model)),
        ElementoTipo: egd.ElementoTipo,
      }),
    );

    const selladoDefault = sellado.value;
    const solapaDefault = solapa.value;

    ordenProduccion.value.OrdenProduccionElemento.forEach((elemento, index) => {
      if (index !== 0) return;
      if (
        elemento.Sellado === 0 &&
        ElementoTipoSeleccionado.value?.CodigoElementoBase === ElementoBase.BOLSAS_BOX
      ) {
        elemento.Sellado = selladoDefault;
      }
      if (
        elemento.Solapa === 0 &&
        [ElementoBase.BOLSAS_FUELLE, ElementoBase.BOLSAS_BOX].includes(
          ElementoTipoSeleccionado.value?.CodigoElementoBase,
        )
      ) {
        elemento.Solapa = solapaDefault;
      }
    });

    await workOrderStore.setDefaults(elementoGrupo.value.Codigo);
  }

  if (ElementoTipoSeleccionado.value?.CodigoElementoBase) {
    await elementoBaseStore.findElementoBaseConfigHTTP(
      ElementoTipoSeleccionado.value.CodigoElementoBase,
      Modules.ORDEN_PRODUCCION,
    );
  }

  await initializeModelo(ordenProduccion.value.OrdenProduccionElemento, Modules.ORDEN_PRODUCCION);

  sincronizarAtributosAdicionales(modelo.value);

  configReady.value = true;

  materiaPrimaFamiliaStore.emptyList();
  materiaPrimaFamiliaStore.findAllfamiliaMaterialHTTP({
    page: 0,
    sortBy: 'Descripcion',
    sort: 'asc',
  });

  unidadMedidaStore.emptyList();
  await unidadMedidaStore.findAllUnidadMedidaHTTP({
    page: 0,
    CodigoElementoTipo: ElementoTipoSeleccionado.value?.Codigo,
    EsUnidadVenta: true,
  });

  usabilidadStore.emptyList();
  await usabilidadStore.findAllUsabilidadHTTP({
    page: 0,
    CodigoElementoTipo: ElementoTipoSeleccionado.value?.Codigo,
  });

  procesoStore.emptyList();
  await procesoStore.findAllProcesosHTTP({
    elementoTipoId: ElementoTipoSeleccionado.value?.Codigo,
    modulo: Modules.ORDEN_PRODUCCION,
  });
}

onBeforeMount(async () => {
  await loadWorkOrder(route.params.id);
});

watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId === oldId) return;
    configReady.value = false;
    resetOrdenProduccion();
    resultadoProduccionOtStore.reset();
    modelo.value = [];
    await loadWorkOrder(newId);
  },
);

onUnmounted(() => {
  resetOrdenProduccion();
  resultadoProduccionOtStore.reset();
});
</script>

<style scoped>
.et-alert-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}

.et-alert-button {
  flex-shrink: 0;
}

.detail-container {
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
}

.custom-label {
  font-weight: 700;
  color: #111;
}

.container {
  display: flex;
  justify-content: flex-end;
}

.input-wrapper {
  display: inline-block;
  margin-right: 10px;
}

/* Marcar con borde rojo aquellos campos obligatorios que no fueron completados */

.quantity-inputs {
  display: flex;
  flex-wrap: nowrap;
}

.container-quantity-inputs {
  display: flex;
  overflow-x: auto;
  width: 100%;
}

/* Estilo para el botón */
.el-button-quantity {
  flex-shrink: 0;
  /* Evita que el botón se reduzca cuando el espacio sea limitado */
  margin: 0;
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.label {
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
}

.cotizacion-link {
  font-weight: 700;
  color: #1677ff;
  text-decoration: none;
}

.cotizacion-link:hover {
  text-decoration: underline;
}
.alerta-banner-dark {
  margin-bottom: 20px;
}

:deep(.el-alert__content) {
  width: 100%;
}

.banner-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #ffffff;
}

.banner-title {
  font-size: 14px;
  font-weight: bold;
}

.banner-description {
  font-size: 13px;
}

.banner-button {
  color: var(--el-color-warning);
  font-weight: 600;
  border: none;
}

.banner-button:hover {
  color: var(--el-color-warning-light-3);
  background-color: var(--el-color-warning-light-9);
}
</style>
